import { Alert, Box, Button, Snackbar, Stack } from "@mui/material";
import AddUsersDialog from "features/addUsersDialog/AddUsersDialog";
import { Grow } from "components/Grow";
import { LoadingScreen } from "components/LoadingScreen";
import { PageToolbar } from "components/PageToolbar";
import { Placeholder } from "components/Placeholder";
import { SearchField } from "components/SearchField";
import ViewUserDialog from "features/viewUserDialog/ViewUserDialog";
import { equalsIgnoringCase, sortUsersBy } from "data/functions";
import {
  AddClaimsForUsersRequest,
  RemoveUsersRequest,
  ServiceSummary,
  ServiceWithClaims,
  SortableColumns,
  SortOrder,
  UpdateUserRequest,
  User,
  UserFilters,
} from "data/interfaces";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiService, Error } from "services";
import UserActions from "./UserActions";
import UserTable from "./UserTable";
import { useAppContext } from "AppContextProvider";
import { useAuth } from "lib/auth";
import { ErrorHandler } from "components/ErrorHandler";
import ActivateServicesDialog from "features/activateServicesDialog/ActivateServicesDialog";
import ImportCsvDialog from "features/importCsvDialog/ImportCsvDialog";
import { FileUpload, PersonAddAlt } from "@mui/icons-material";

const UsersPage = () => {
  const { accountId } = useParams();
  const { state } = useAppContext();

  const { user } = useAuth();
  const isPrimaryAdmin =
    state.activeAccount &&
    user &&
    user.profile.email !== undefined &&
    equalsIgnoringCase(user.profile.email, state.activeAccount.admin);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [notifyMessage, setNotifyMessage] = useState<string>();

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [services, setServices] = useState<ServiceSummary[]>([]);

  const [order, setOrder] = useState<SortOrder>("asc");
  const [orderBy, setOrderBy] = useState<keyof SortableColumns>("name");
  const [page, setPage] = useState(0);

  const [addUsersDialogOpen, setAddUsersDialogOpen] = useState(false);
  const [importCsvDialogOpen, setImportCsvDialogOpen] = useState(false);
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [activateServicesDialogOpen, setActivateServicesDialogOpen] =
    useState(false);

  const sortedUsers = sortUsersBy(orderBy, order, users);

  const [filters, setFilters] = useState<UserFilters>({ search: "" });
  const filteredUsers = sortedUsers.filter((user) => {
    let searchString = filters.search?.toLowerCase();
    return (
      searchString == null ||
      (user.name || "").toLowerCase().includes(searchString) ||
      user.email.toLowerCase().includes(searchString)
    );
  });

  useEffect(() => {
    accountId &&
      ApiService.getServices(accountId)
        .then((services: ServiceSummary[]) => {
          ApiService.getUsers(accountId)
            .then((users: User[]) => {
              setServices(services);
              setUsers(users);
              setLoading(false);
            })
            .catch((error: Error) => setError(error));
        })
        .catch((error: Error) => setError(error));
  }, [accountId]);

  const handleSort = (column: string) => {
    const isAlreadySelectedAndAscending = orderBy === column && order === "asc";
    setOrder(isAlreadySelectedAndAscending ? "desc" : "asc");
    setOrderBy(column as keyof SortableColumns);
    setPage(0);
  };

  const handleSelect = (selected: boolean, user: User) => {
    setSelectedUsers(
      selected
        ? [...selectedUsers, user]
        : selectedUsers.filter((u) => u.email !== user.email),
    );
  };

  const handleSelectAll = (allSelected: boolean) => {
    setSelectedUsers(allSelected ? filteredUsers : []);
  };

  const handleRemoveUsers = (selectedUsers: User[]) => {
    const deletedUsers = selectedUsers.filter(
      (s) => s.accountStatus === "Verified",
    );
    const deletedUnregisteredUsers = selectedUsers.filter(
      (s) => s.accountStatus === "Pending",
    );

    const removeUsersRequest: RemoveUsersRequest = {
      userIds: deletedUsers.map((u) => u.id),
      unregisteredUsers: deletedUnregisteredUsers.map((u) => u.email),
    };

    accountId &&
      ApiService.removeUsers(accountId, removeUsersRequest)
        .then(() => {
          let remainingUsers = users.filter(
            (u) =>
              u.accountStatus === "Verified" &&
              !deletedUsers.some((uu) => uu.id === u.id),
          );
          let remainingUnregisteredUsers = users.filter(
            (u) =>
              u.accountStatus === "Pending" &&
              !deletedUnregisteredUsers.some((uu) => uu.email === u.email),
          );
          setUsers(remainingUsers.concat(remainingUnregisteredUsers));
          setSelectedUsers([]);
          ApiService.getServices(accountId)
            .then((services: ServiceSummary[]) => {
              setServices(services);
            })
            .catch((error: Error) =>
              setNotifyMessage(
                "Failed to update services statuses. Please refresh the page.",
              ),
            );
        })
        .catch((error: Error) => setNotifyMessage("Failed to disable users."));
  };

  const handleSetAdminAccess = (selectedUsers: User[], isAdmin: boolean) => {
    const userEmails = selectedUsers.map((user) => user.email);
    accountId &&
      ApiService.setAdminAccess(accountId, userEmails, isAdmin)
        .then(() => {
          setUsers(
            users.map((user) =>
              userEmails.includes(user.email) ? { ...user, isAdmin } : user,
            ),
          );
          setSelectedUsers([]);
        })
        .catch((error: Error) =>
          setNotifyMessage("Failed to change admin access for the users."),
        );
  };

  const handleFilterChange = (filters: UserFilters) => {
    setFilters(filters);
    setPage(0);
    setSelectedUsers([]);
  };

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setViewUserDialogOpen(true);
  };

  const handleAddUsers = (newUsers: string[]) => {
    accountId &&
      ApiService.AddUsers(accountId, newUsers)
        .then((updatedUsers: User[]) => {
          setAddUsersDialogOpen(false);
          setUsers(updatedUsers);
          ApiService.getServices(accountId)
            .then((services: ServiceSummary[]) => {
              setServices(services);
            })
            .catch((error: Error) =>
              setNotifyMessage(
                "Failed to update services statuses. Please refresh the page.",
              ),
            );
        })
        .catch((error: Error) => {
          setAddUsersDialogOpen(false);
          setNotifyMessage("Failed to add new users.");
        });
  };

  const handleImportCsv = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    accountId &&
      ApiService.AddUsersByCsv(accountId, formData)
        .then((updatedUsers: User[]) => {
          setImportCsvDialogOpen(false);
          setUsers(updatedUsers);
        })
        .catch((error: Error) => {
          setImportCsvDialogOpen(false);
          setNotifyMessage("Failed to invite users.");
        });
  };

  const handleSaveUser = (updatedUser: User) => {
    const updateClaimsRequests: UpdateUserRequest[] = [
      {
        email: updatedUser.email,
        id: updatedUser.id,
        admin: updatedUser.isAdmin,
        serviceClaims: updatedUser.services.map((s) => ({
          serviceReference: s.serviceReference,
          licenseReference: s.licenseReference,
          claims: s.claims,
        })),
      },
    ];

    accountId &&
      ApiService.updateClaimsFor(accountId, updateClaimsRequests)
        .then((updatedServices: ServiceSummary[]) => {
          if (updatedUser.id !== null) {
            setUsers(
              users.map((user) =>
                user.id === updatedUser.id ? updatedUser : user,
              ),
            );
          } else {
            setUsers(
              users.map((user) =>
                user.email === updatedUser.email ? updatedUser : user,
              ),
            );
          }
          setServices(updatedServices);
        })
        .catch((error: Error) =>
          setNotifyMessage("Failed to add roles for the user."),
        );

    setSelectedUser(undefined);
    setViewUserDialogOpen(false);
  };

  const handleActivateServices = (
    selectedServiceWithClaims: ServiceWithClaims[],
  ) => {
    const addClaimsRequest: AddClaimsForUsersRequest = {
      users: selectedUsers.map((u) => ({ email: u.email, userId: u.id })),
      serviceClaims: selectedServiceWithClaims,
    };

    accountId &&
      ApiService.addClaimsForUsers(accountId, addClaimsRequest)
        .then((updatedServices: ServiceSummary[]) => {
          setServices(updatedServices);
          ApiService.getUsers(accountId).then((users: User[]) => {
            setUsers(users);
          });
        })
        .catch((error: Error) => {
          setNotifyMessage("Failed to activate services.");
        })
        .finally(() => {
          setActivateServicesDialogOpen(false);
          handleSelectAll(false);
        });
  };

  return (
    <ErrorHandler error={error}>
      <Box p={3}>
        {loading && <LoadingScreen title="Loading..." />}

        {!loading && (
          <>
            <PageToolbar>
              <Stack spacing={2} direction="row">
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<PersonAddAlt />}
                  onClick={() => setAddUsersDialogOpen(true)}
                >
                  Invite users by email
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<FileUpload />}
                  onClick={() => setImportCsvDialogOpen(true)}
                >
                  Import users from CSV file
                </Button>
              </Stack>
              <Grow />
              <Stack spacing={2} direction="row">
                <UserActions
                  selectedUsers={selectedUsers}
                  account={state.activeAccount}
                  primaryAdmin={isPrimaryAdmin ?? false}
                  services={services}
                  onRemoveUsers={handleRemoveUsers}
                  onActivateServices={() => setActivateServicesDialogOpen(true)}
                  onSetAdminAccess={handleSetAdminAccess}
                />
                <SearchField
                  value={filters.search}
                  onChange={(event: any) =>
                    handleFilterChange({
                      ...filters,
                      search: event.target.value,
                    })
                  }
                />
              </Stack>
            </PageToolbar>

            {users.length === 0 ? (
              <Placeholder>No users found</Placeholder>
            ) : (
              <UserTable
                users={filteredUsers}
                selected={selectedUsers}
                account={state.activeAccount}
                onSelect={handleSelect}
                onSelectAll={handleSelectAll}
                orderBy={orderBy}
                order={order}
                onSort={handleSort}
                page={page}
                onPageChange={(page: number) => setPage(page)}
                onSelectUser={handleSelectUser}
              />
            )}

            {addUsersDialogOpen && (
              <AddUsersDialog
                users={users}
                open={addUsersDialogOpen}
                onAddUsers={handleAddUsers}
                onClose={() => setAddUsersDialogOpen(false)}
              />
            )}

            {importCsvDialogOpen && (
              <ImportCsvDialog
                open={importCsvDialogOpen}
                onImportCsv={handleImportCsv}
                onClose={() => setImportCsvDialogOpen(false)}
              />
            )}

            {viewUserDialogOpen && selectedUser && (
              <ViewUserDialog
                user={selectedUser}
                availableServices={services.filter(
                  (s) => s.seatsTotal > 0 || s.unlimitedSeats,
                )}
                onConfirm={handleSaveUser}
                onClose={() => setViewUserDialogOpen(false)}
              />
            )}

            {activateServicesDialogOpen && (
              <ActivateServicesDialog
                open={activateServicesDialogOpen}
                users={selectedUsers}
                services={services.filter(
                  (s) => s.seatsTotal > 0 || s.unlimitedSeats,
                )}
                onActivateServices={handleActivateServices}
                onClose={() => setActivateServicesDialogOpen(false)}
              />
            )}
          </>
        )}
      </Box>

      <Snackbar
        open={notifyMessage !== undefined}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={8000}
        onClose={() => setNotifyMessage(undefined)}
      >
        <Alert onClose={() => setNotifyMessage(undefined)} severity="error">
          {notifyMessage}
        </Alert>
      </Snackbar>
    </ErrorHandler>
  );
};

export default UsersPage;
